import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { MessageCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { apiService } from "@/services/api";
import { useTranslation } from "react-i18next";
import { H } from 'highlight.run';


const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await apiService.login({ email, password });
      const { user, token } = response.data;

      if (user?.admin) {
        localStorage.setItem("isAdmin", "true");
      }
      
      try {
        H.identify(user.email, {
          id: user.id,
          phone: user.phone,
          name: user.name,
          email: user.email,
          admin: user.admin,
          active: user.active,
        });
      } catch (error) {
        console.error("Error sending user data to Highlight:", error);
      }

      // Invalidate and refetch user data
      await queryClient.invalidateQueries({ queryKey: ["user"] });

      toast({
        title: t("common.success"),
        description: t("login.success"),
      });

      navigate("/dashboard");
    } catch (error) {
      toast({
        title: t("common.error"),
        description:
          error instanceof Error ? error.message : t("login.invalid_credentials"),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <MessageCircle className="h-12 w-12 text-whatsapp" />
          </div>
          <CardTitle className="text-2xl text-center">{t("login.welcome_back")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="email"
                placeholder={t("login.email_placeholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Input
                type="password"
                placeholder={t("login.password_placeholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-whatsapp hover:bg-whatsapp/90"
            >
              {t("login.sign_in")}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <a href="/register" className="text-whatsapp hover:underline">
              {t("login.no_account")}
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
